import React, { useContext, useEffect, useState } from "react";
import {
  IAddressesAndContactsComponent,
  ICustomerSelectedAddressAndContact,
} from "./AddressesAndContactsComponent.types";
import {
  ContextualMenu,
  DefaultButton,
  IDragOptions,
  IIconProps,
  IconButton,
  Link,
  MessageBar,
  Modal,
  Pivot,
  PivotItem,
  PrimaryButton,
  Separator,
  Stack,
} from "@fluentui/react";
import { useId, useBoolean } from "@fluentui/react-hooks";
import {
  contentStyles,
  iconButtonStyles,
} from "./AddressesAndContactsComponentStyle";
import {
  ADDRESS,
  BILL_TO,
  CONTACT,
  SELECT_ACTION,
  SHIP_TO,
} from "../../Common/QuotationData";
import { QuotationContext } from "../../QuotationContext";
import { QuotationInfoType } from "../../Quotation.types";
import { SeparatorStyles, stackTokensWithGap30 } from "../../QuotationStyle";
import CarouselComponent from "../CarouselComponent/CarouselComponent";
import { useNavigate } from "react-router";
export const AddressesAndContactsComponent: React.FunctionComponent<
  IAddressesAndContactsComponent
> = ({ showList, setShowList }) => {
  const cancelIcon: IIconProps = { iconName: "Cancel" };
  const titleId = useId("title");
  const { quotationInfo, setQuotationInfo } = useContext(
    QuotationContext
  ) as QuotationInfoType;
  const [isDraggable, { toggle: toggleIsDraggable }] = useBoolean(true);
  const [selectedKey, setSelectedKey] = React.useState<string>(BILL_TO);
  const handlePivotChange = (
    item?: PivotItem,
    ev?: React.KeyboardEvent<HTMLElement> | React.MouseEvent<HTMLElement, MouseEvent>
  ) => {
    setSelectedKey(item?.props.itemKey ?? "");
  };
  const [messageBar, setMessageBar] = useState(true);
  const [selectedBillTo, setSelectedBillTo] =
    useState<ICustomerSelectedAddressAndContact>({
      address: quotationInfo.customer.selectedBillToAddress,
      contact: quotationInfo.customer.selectedBillToContact,
    });
  const [selectedShipTo, setSelectedShipTo] =
    useState<ICustomerSelectedAddressAndContact>({
      address: quotationInfo.customer.selectedShipToAddress,
      contact: quotationInfo.customer.selectedShipToContact,
    });

  const navigate = useNavigate();
  const handleCustomerClick = () => {
    navigate("/CustomerEditContact", {
      state: { id: quotationInfo?.customer.id, name: quotationInfo?.customer.name },
    });
  };

  const dragOptions: IDragOptions = {
    moveMenuItemText: "Move",
    closeMenuItemText: "Close",
    menu: ContextualMenu,
    dragHandleSelector: ".ms-Modal-scrollableContent > div:first-child",
  };
  const closeModal = () => {
    toggleIsDraggable();
    setShowList(false);
  };
  const onClickSaveButton = () => {
    setQuotationInfo({
      ...quotationInfo,
      customer: {
        ...quotationInfo.customer,
        selectedBillToAddress: selectedBillTo.address,
        selectedBillToContact: selectedBillTo.contact,
        selectedShipToAddress: selectedShipTo.address,
        selectedShipToContact: selectedShipTo.contact,
      },
    });
    closeModal();
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      const elements = document.querySelectorAll(".screenReaderText-143");
      if (elements) {
        elements.forEach((element) =>
          element.setAttribute("aria-hidden", "true")
        );
      }
    }, 1000);
    return () => clearTimeout(timer);
  }, []);

  const CustomerMessageBar = () => {
    return (
      <MessageBar
        isMultiline
        styles={{
          root: {
            // Add custom styling to ensure text wraps
            marginTop: "1%",
            marginBottom: "2%",
            whiteSpace: "normal", // Allow wrapping
            wordWrap: "break-word", // Break long words
            maxWidth: "50%", // Set message bar width on the basis of it's content
          },
        }}
        onDismiss={() => setMessageBar(false)}
        dismissButtonAriaLabel="Close"
      >
        Add new address/contact from&nbsp;
        <Link onClick={handleCustomerClick}>Customers&nbsp;</Link>
        page.
      </MessageBar>
    )
  };
  return (
    <Stack
      verticalAlign="start"
      style={{
        position: "absolute",
        right: 24,
        top: 48,
        zIndex: 2,
      }}
    >
      <Modal
        titleAriaId={titleId}
        isOpen={showList}
        onDismiss={closeModal}
        isClickableOutsideFocusTrap
        isModeless={true}
        containerClassName={contentStyles.container}
        dragOptions={isDraggable ? dragOptions : undefined}
        closeButtonAriaLabel="close"
        aria-label="titleId"
        aria-labelledby="titleId"
      >
        <Stack horizontal horizontalAlign="space-between">
          <IconButton
            styles={iconButtonStyles}
            iconProps={cancelIcon}
            ariaLabel="Close popup modal"
            onClick={closeModal}
          />
        </Stack>
        <Stack
          verticalFill
          verticalAlign="start"
          style={{ marginLeft: "3%", marginRight: "3%", marginBottom: "5%" }}
        >
          <Pivot
            aria-label={BILL_TO}
            onLinkClick={handlePivotChange}
            selectedKey={selectedKey}
          >
            <PivotItem headerText={BILL_TO} itemKey={BILL_TO}>
              {CustomerMessageBar()}
              <CarouselComponent
                title={`${SELECT_ACTION} ${ADDRESS}`}
                aria-label={`${SELECT_ACTION} ${ADDRESS}`}
                items={quotationInfo.customer.billTo}
                selectedKey={
                  quotationInfo.customer.selectedBillToAddress.addressId
                }
                selection={selectedBillTo}
                setSelection={setSelectedBillTo}
              />
              <Separator styles={SeparatorStyles} />
              <CarouselComponent
                title={`${SELECT_ACTION} ${CONTACT}`}
                aria-label={`${SELECT_ACTION} ${CONTACT}`}
                items={quotationInfo.customer.customerContacts}
                selectedKey={quotationInfo.customer.selectedBillToContact.id}
                selection={selectedBillTo}
                setSelection={setSelectedBillTo}
              />
            </PivotItem>
            <PivotItem headerText={SHIP_TO} itemKey={SHIP_TO}>
              {CustomerMessageBar()}
              <CarouselComponent
                title={`${SELECT_ACTION} ${ADDRESS}`}
                items={quotationInfo.customer.shipTo}
                selectedKey={
                  quotationInfo.customer.selectedShipToAddress.addressId
                }
                selection={selectedShipTo}
                setSelection={setSelectedShipTo}
                aria-label="Set selected shipTo"
              />
              <Separator styles={SeparatorStyles} />
              <CarouselComponent
                title={`${SELECT_ACTION} ${CONTACT}`}
                items={quotationInfo.customer.customerContacts}
                selectedKey={quotationInfo.customer.selectedShipToContact.id}
                selection={selectedShipTo}
                setSelection={setSelectedShipTo}
                aria-label="Set selected shipTo Contact"
              />
            </PivotItem>
          </Pivot>
        </Stack>
        <Stack
          horizontal
          horizontalAlign="center"
          tokens={stackTokensWithGap30}
          style={{ marginBottom: "2%" }}
        >
          <DefaultButton
            text="Cancel"
            type="button"
            onClick={() => {
              closeModal();
            }}
          />
          <PrimaryButton
            text="Save"
            type="button"
            onClick={onClickSaveButton}
          />
        </Stack>
      </Modal>
    </Stack>
  );
};
